/**
 * Author: Valeria Sinnati
 *  /\___/\
 * ꒰ ˶• ༝ - ˶꒱   Marchio grafico (l'occhio) della BI, da affiancare a
 * ./づ~ ♡       `brand.name`: è solo il segno, il NOME resta dinamico e non
 *               compare mai qui dentro.
 *
 * Path copiati tali e quali da docs/mockups/diana/assets/logo/occhio.svg.
 * `fill="currentColor"`: il colore lo decide chi lo usa con una classe di
 * testo semantica (`text-accent-ink` su fondo neutro, `text-strong-accent`
 * dentro una card forte), così segue il tema senza varianti.
 *
 * Di default è decorativo (`aria-hidden`), perché accanto c'è già il nome.
 * Se sta da solo (es. un pulsante con la sola icona) si passa `label`, che
 * diventa l'`aria-label` dell'immagine.
 */
import type { SVGAttributes } from 'react'

export interface BrandMarkProps extends Omit<SVGAttributes<SVGSVGElement>, 'width' | 'height'> {
  /** Larghezza in px; l'altezza segue il rapporto del disegno (773 × 408). */
  size?: number
  /** Nome accessibile: se assente il marchio è decorativo. */
  label?: string
}

const VIEWBOX_W = 773.15
const VIEWBOX_H = 408.03

export function BrandMark({ size = 32, label, className = '', ...props }: BrandMarkProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
      width={size}
      height={(size * VIEWBOX_H) / VIEWBOX_W}
      fill="currentColor"
      className={`inline-block shrink-0 ${className}`}
      role="img"
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      {...props}
    >
      <path d="M765.72,186.75c-62.1-61.98-125.05-108.66-188.37-139.85C514,15.71,450.27,0,386.61,0s-127.42,15.71-190.74,46.9C132.51,78.09,69.57,124.77,7.43,186.75c-4.93,4.93-7.43,11.09-7.43,17.27s2.5,12.3,7.43,17.27c42.56,42.43,85.49,77.68,128.7,105.69,4.22,2.72,8.4,5.37,12.55,7.99,2.09,1.28,4.18,2.56,6.28,3.81,2.12,1.25,4.22,2.5,6.31,3.72.5.25.94.53,1.41.81,2.34,1.37,4.65,2.68,6.99,4,4.9,2.75,9.8,5.43,14.74,7.96,1.81.97,3.65,1.9,5.5,2.87,1.84.94,3.68,1.84,5.53,2.75,1.47.72,2.93,1.44,4.4,2.15.34.19.72.38,1.12.53,1.84.88,3.68,1.75,5.5,2.62,4.93,2.28,9.84,4.46,14.77,6.56,2.44,1.03,4.9,2.06,7.37,3.06,1.62.69,3.28,1.34,4.9,2,1.65.66,3.28,1.28,4.9,1.91,5.15,1.97,10.3,3.84,15.49,5.59,2.81.97,5.65,1.91,8.46,2.81,2.81.87,5.62,1.75,8.43,2.59,38.56,11.52,77.22,17.33,115.84,17.33,51.08,0,102.16-10.12,153.12-30.22l27.7-13.68c-1.41-3.53-2.81-6.78-4.12-9.34-7.15-13.36-23.29-21.48-41.03-27.82-.75.31-1.53.62-2.28.94-44.84,18.36-89.36,27.63-133.39,27.63-48.9,0-98.38-11.37-148.22-34v-26.57c0-108.6,54.95-173.57,150.65-173.57,84.43,0,137.2,50.58,148.44,137.1,12.11-5.81,21.08-12.77,26.79-22.95,3.15-5.59,6.12-13.74,8.96-21.64,1.5-4.09,2.9-7.99,4.4-11.68,3.75-9.96,10.71-16.99,19.3-19.8-18.23-48.71-49.55-85.58-92.3-108.28,58.95,21.42,118.46,58.7,178.07,111.62,11.37,10.09,11.37,26.35,0,36.44-9.43,8.37-18.86,16.36-28.29,23.95,10.55,10.05,26.01,15.96,42.4,21.51,3.22,1.12,7.65,3,11.86,5.99,19.11-16.02,38.09-33.5,57.05-52.39,5-4.96,7.43-11.12,7.43-17.27s-2.44-12.33-7.43-17.27ZM162.99,278.92c-24.1-16.3-48.3-35.22-72.5-56.7-11.37-10.09-11.37-26.35,0-36.44,64.88-57.58,129.61-96.67,193.68-116.96-74.16,33.94-117.15,107.6-121.18,210.1Z" />
      <path d="M702.09,299.09c-4.78,7.65-25.26,9.68-44.37,20.73-8.74,4.96-17.33,12.33-23.57,21.64l-.5.72c-10.93,13.99-17.17,47.05-25.04,50.43-.87.31-1.72.44-2.56.44-5.37,0-9.93-5.78-11.49-10.74-2.93-7.09-6.59-17.52-10.27-26.48-1.47-3.56-2.93-6.87-4.37-9.68-7.24-13.68-19.92-22.89-34.16-29.76-8.62-4.15-17.83-7.46-26.73-10.34-4.46-1.59-10.37-4.78-10.4-9.77-.09-6.49,8.18-9.84,13.68-11.65,23.64-8.06,45.9-17.83,58.29-39.9,2-3.59,3.84-7.71,5.59-12.02,3.22-8.03,6.09-16.74,8.9-23.7,1.47-3.97,3.97-7.62,7.77-8.8.69-.22,1.41-.34,2.19-.41h.31c6.03,0,9.02,5.87,10.96,11.27,4.12,10.49,8.09,24.29,13.71,34.13,2.69,4.75,5.9,8.9,9.52,12.61,13.33,13.77,32.28,21.17,51.11,27.57.78.28,1.62.59,2.5,1,5.46,2.4,11.65,6.93,8.93,12.71Z" />
    </svg>
  )
}
